/**
 * FORMATTERS - Utility functions for number formatting
 * 
 * Corrige problemas de floating point errors no JavaScript
 * (ex: 3.500000000000004% → 3.5%)
 * 
 * @module utils/formatters
 */

/**
 * Formata percentual com decimais fixos
 * @param value Valor em escala 0-100 (ex: 3.5 para 3.5%)
 * @param decimals Número de casas decimais (default: 1)
 * @returns String formatada (ex: "3.5")
 * 
 * @example
 * formatPercentage(3.500000000000004) // "3.5"
 * formatPercentage(15.25, 2) // "15.25"
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return value.toFixed(decimals);
};

/**
 * Formata número com decimais fixos
 * @param value Valor numérico
 * @param decimals Número de casas decimais (default: 2)
 * @returns String formatada
 * 
 * @example
 * formatNumber(3.5000) // "3.50"
 * formatNumber(123.456789, 3) // "123.457"
 */
export const formatNumber = (value: number, decimals: number = 2): string => {
  return value.toFixed(decimals);
};

/**
 * Formata valor monetário em BRL
 * @param value Valor em reais
 * @param showCents Se deve mostrar centavos (default: false)
 * @returns String formatada (ex: "R$ 1.234.567")
 * 
 * @example
 * formatCurrency(1234567) // "R$ 1.234.567"
 * formatCurrency(1234.56, true) // "R$ 1.234,56"
 */
export const formatCurrency = (value: number, showCents: boolean = false): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(value);
};

/**
 * Formata número grande com abreviação (K, M, B)
 * @param value Valor numérico
 * @param decimals Número de casas decimais (default: 1)
 * @returns String formatada (ex: "1.2M", "500K")
 * 
 * @example
 * formatCompactNumber(1200000) // "1.2M"
 * formatCompactNumber(500000) // "500K"
 * formatCompactNumber(1500000000) // "1.5B"
 */
export const formatCompactNumber = (value: number, decimals: number = 1): string => {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(decimals)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(decimals)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(decimals)}K`;
  }
  return value.toFixed(decimals);
};

/**
 * Formata valor como percentual em escala decimal
 * @param value Valor em escala 0-1 (ex: 0.035 para 3.5%)
 * @param decimals Número de casas decimais (default: 1)
 * @returns String formatada (ex: "3.5%")
 * 
 * @example
 * formatPercentFromDecimal(0.035) // "3.5%"
 * formatPercentFromDecimal(0.1525, 2) // "15.25%"
 */
export const formatPercentFromDecimal = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Formata fator multiplicador (ex: 0.85 → "85%")
 * @param value Valor em escala 0-1
 * @param decimals Número de casas decimais (default: 0)
 * @returns String formatada (ex: "85%")
 * 
 * @example
 * formatFactor(0.85) // "85%"
 * formatFactor(0.735, 1) // "73.5%"
 */
export const formatFactor = (value: number, decimals: number = 0): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};
